import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { tileService } from "../../services/enhancedCrudService";
import Select from "react-select";
import Pagination from "../../components/Pagination";

const UserList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tileSizes, setTileSizes] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedUserForVerify, setSelectedUserForVerify] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrevious: false,
    next: null,
    previous: null
  });
  
  // Initialize filters from URL parameters or defaults
  const getInitialFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      limit: parseInt(searchParams.get('limit')) || 10,
      page: parseInt(searchParams.get('page')) || 1,
      user_type: searchParams.get('user_type') || "all",
      subscription_status: searchParams.get('subscription_status') || "all",
      verify_status: searchParams.get('verify_status') || "all",
      firm_name: searchParams.get('firm_name') || "",
      mobile_number: searchParams.get('mobile_number') || "",
      order: searchParams.get('order') || "",
      size: searchParams.get('size') || "all",
    };
  };
  
  const [filters, setFilters] = useState(getInitialFilters());

  // Function to update URL parameters when filters change
  const updateURL = (newFilters) => {
    const searchParams = new URLSearchParams();
    
    // Only add non-default values to URL
    if (newFilters.limit !== 10) searchParams.set('limit', newFilters.limit);
    if (newFilters.page !== 1) searchParams.set('page', newFilters.page);
    if (newFilters.user_type !== "all") searchParams.set('user_type', newFilters.user_type);
    if (newFilters.subscription_status !== "all") searchParams.set('subscription_status', newFilters.subscription_status);
    if (newFilters.verify_status !== "all") searchParams.set('verify_status', newFilters.verify_status);
    if (newFilters.firm_name) searchParams.set('firm_name', newFilters.firm_name);
    if (newFilters.mobile_number) searchParams.set('mobile_number', newFilters.mobile_number);
    if (newFilters.order) searchParams.set('order', newFilters.order);
    if (newFilters.size !== "all") searchParams.set('size', newFilters.size);
    
    const newURL = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(`/user-list${newURL}`, { replace: true });
  };

  // Enhanced setFilters function that also updates URL
  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    updateURL(newFilters);
  };

  const userTypeOptions = [
    { value: "all", label: "All Users" },
    { value: "buyer", label: "Buyers" },
    { value: "seller", label: "Sellers" },
  ];

  const subscriptionStatusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "expired", label: "Expired" },
  ];

  const verifyStatusOptions = [
    { value: "all", label: "All Verification" },
    { value: "true", label: "Verified" },
    { value: "false", label: "Unverified" },
  ];

  const sortOptions = [
    { value: "user__created_at", label: "Ascending by Created Date" },
    { value: "-user__created_at", label: "Descending by Created Date" },
  ];

  const fetchTileSizes = async () => {
    try {
      const response = await tileService.getSizes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setTileSizes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const getSizeOptions = () => {
    const options = [{ value: "all", label: "All Sizes" }];
    tileSizes.forEach(size => {
      if (size.label && size.value) {
        options.push({ value: size.value.toString(), label: size.label });
      }
    });
    return options;
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.user_type && filters.user_type !== "all")
        params.append("user_type", filters.user_type);
      if (filters.subscription_status && filters.subscription_status !== "all")
        params.append("subscription_status", filters.subscription_status);
      if (filters.verify_status && filters.verify_status !== "all")
        params.append("verify_status", filters.verify_status);
      if (filters.firm_name && filters.firm_name.trim() !== "")
        params.append("firm_name", filters.firm_name.trim());
      if (filters.mobile_number && filters.mobile_number.trim() !== "")
        params.append("mobile_number", filters.mobile_number.trim());
      if (filters.page && filters.page !== "")
        params.append("page", filters.page);
      if (filters.order && filters.order !== "")
        params.append("order", filters.order);
      if (filters.size && filters.size !== "all")
        params.append("size", filters.size);
   
      const queryString = params.toString();
      const url = queryString
        ? `${API_ENDPOINTS.ACCOUNTS.USER_LIST}?${queryString}`
        : API_ENDPOINTS.ACCOUNTS.USER_LIST;
      
      const response = await apiService.get(url);
      
      // Handle pagination data
      if (response.count !== undefined) {
        const totalPages = Math.ceil(response.count / filters.limit);
        setPagination({
          currentPage: filters.page,
          totalPages: totalPages,
          totalCount: response.count,
          hasNext: response.next,
          hasPrevious: response.previous,
          next: response.next,
          previous: response.previous
        });
      }
      
      if (response.results) {
        const usersData = response.results || [];
        
        const validatedUsers = usersData.map(user => ({
          ...user,
          is_verified: user.is_verified || false,
          is_disabled: user.is_disabled || false,
          id: user.id || user.user_id || Math.random().toString(36).substr(2, 9)
        }));
        
        setUsers(validatedUsers);
      } else {
        toast.error(response.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "N/A";
    // Format as (+91)XXXXXXXXXX
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(+91)${cleaned}`;
    }
    return phoneNumber;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getUserTypeBadge = (userType) => {
    const type = userType?.toLowerCase();
    if (type === "buyer")
      return <span className="badge bg-primary">Buyer</span>;
    if (type === "seller")
      return <span className="badge bg-success">Seller</span>;
    return <span className="badge bg-secondary">Unknown</span>;
  };


  const getSubscriptionStatusBadge = (status) => {
    if (!status) return <span className="badge bg-secondary">N/A</span>;

    const statusLower = status.toLowerCase();
    if (statusLower === "active")
      return <span className="badge bg-success">Active</span>;
    if (statusLower === "inactive")
      return <span className="badge bg-danger">Inactive</span>;
    if (statusLower === "expired")
      return <span className="badge bg-warning">Expired</span>;
    if (statusLower === "pending")
      return <span className="badge bg-info">Pending</span>;
    if (statusLower === "not_purchased")
      return <span className="badge bg-secondary">NOT_PURCHASED</span>;

    return <span className="badge bg-secondary">{status}</span>;
  };

  const getActiveStatusBadge = (isDisabled) => {
    if (isDisabled) {
      return <span className="badge bg-danger">Inactive</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  const getVerificationStatusBadge = (isVerified) => {
    if (isVerified) {
      return <span className="badge bg-success">Verified</span>;
    }
    return <span className="badge bg-warning">Unverified</span>;
  };

  const handleEditUser = (user) => {
    // Create URL with current filter state to preserve pagination
    const searchParams = new URLSearchParams();
    if (filters.page !== 1) searchParams.set('page', filters.page);
    if (filters.limit !== 10) searchParams.set('limit', filters.limit);
    if (filters.user_type !== "all") searchParams.set('user_type', filters.user_type);
    if (filters.subscription_status !== "all") searchParams.set('subscription_status', filters.subscription_status);
    if (filters.verify_status !== "all") searchParams.set('verify_status', filters.verify_status);
    if (filters.firm_name) searchParams.set('firm_name', filters.firm_name);
    if (filters.mobile_number) searchParams.set('mobile_number', filters.mobile_number);
    if (filters.order) searchParams.set('order', filters.order);
    if (filters.size !== "all") searchParams.set('size', filters.size);
    
    const returnUrl = searchParams.toString() ? `?${searchParams.toString()}` : '';
    
    if (user.user_type === "SELLER") {
      navigate(`/user-seller/${user.id}?returnUrl=${encodeURIComponent(returnUrl)}`);
    } else {
      navigate(`/user-buyer/${user.id}?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  };

  const handleToggleStatus = (user) => {
    if (!user || typeof user !== 'object') {
      console.error("Invalid user object:", user);
      toast.error("Invalid user data");
      return;
    }
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const confirmToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      setShowConfirmModal(false);
      const profileId = selectedUser.id;
      const role = selectedUser.user_type === "SELLER" ? "seller" : "buyer";
      if (!profileId) {
        toast.error("Profile ID not found for this user");
        return;
      }
      const url = API_ENDPOINTS.ACCOUNTS.PROFILE_ACTIVE_STATUS(profileId, role);
      const queryParams = `?is_disabled=${!selectedUser.is_disabled}&role=${role}`;
      const fullUrl = `${url}${queryParams}`;      
      await apiService.patch(fullUrl, {});
      setUsers(prevUsers => {
        if (!Array.isArray(prevUsers)) {
          console.error("prevUsers is not an array:", prevUsers);
          return prevUsers;
        }
        
        return prevUsers.map(u => {
          if (!u || typeof u !== 'object') {
            console.error("Invalid user object:", u);
            return u;
          }
          return u.id === selectedUser.id 
            ? { ...u, is_disabled: !u.is_disabled }
            : u;
        });
      });
      toast.success(`User ${selectedUser.is_disabled ? 'activated' : 'deactivated'} successfully`);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Failed to update user status");
    } finally {
      setLoading(false);
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedUser(null);
  };

  const handleToggleVerification = (user) => {
    if (!user || typeof user !== 'object') {
      console.error("Invalid user object:", user);
      toast.error("Invalid user data");
      return;
    }
    setSelectedUserForVerify(user);
    setShowVerifyModal(true);
  };

  const confirmToggleVerification = async () => {
    if (!selectedUserForVerify) return;

    try {
      setLoading(true);
      setShowVerifyModal(false);
      const userId = selectedUserForVerify.user_id;
      if (!userId) {
        toast.error("User ID not found for this user");
        return;
      }
      const url = API_ENDPOINTS.ACCOUNTS.USER_VERIFY_STATUS(userId);
      const currentVerified = Boolean(selectedUserForVerify.is_verified);
      const isVerifiedValue = !currentVerified;
      const queryParams = `?is_verified=${isVerifiedValue}`;
      const fullUrl = `${url}${queryParams}`;
      await apiService.patch(fullUrl, {});
      
      setUsers(prevUsers => {
        if (!Array.isArray(prevUsers)) {
          console.error("prevUsers is not an array:", prevUsers);
          return prevUsers;
        }
        
        return prevUsers.map(u => {
          if (!u || typeof u !== 'object') {
            console.error("Invalid user object:", u);
            return u;
          }
          return u.user_id === selectedUserForVerify.user_id 
            ? { ...u, is_verified: !u.is_verified }
            : u;
        });
      });
      
      toast.success(`User ${selectedUserForVerify.is_verified ? 'unverified' : 'verified'} successfully`);
      setSelectedUserForVerify(null);
    } catch (error) {
      console.error("Error updating user verification status:", error);
      toast.error("Failed to update user verification status");
    } finally {
      setLoading(false);
    }
  };

  const closeVerifyModal = () => {
    setShowVerifyModal(false);
    setSelectedUserForVerify(null);
  };

  const handleExportUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.user_type && filters.user_type !== "all")
        params.append("user_type", filters.user_type);
      if (filters.subscription_status && filters.subscription_status !== "all")
        params.append("subscription_status", filters.subscription_status);
      if (filters.verify_status && filters.verify_status !== "all")
        params.append("verify_status", filters.verify_status);
      if (filters.firm_name && filters.firm_name.trim() !== "")
        params.append("firm_name", filters.firm_name.trim());
      if (filters.mobile_number && filters.mobile_number.trim() !== "")
        params.append("mobile_number", filters.mobile_number.trim());
      if (filters.order && filters.order !== "")
        params.append("order", filters.order);
      if (filters.size && filters.size !== "all")
        params.append("size", filters.size);
   
      const queryString = params.toString();
      const url = queryString
        ? `${API_ENDPOINTS.ACCOUNTS.USER_LIST_EXPORT}?${queryString}`
        : API_ENDPOINTS.ACCOUNTS.USER_LIST_EXPORT;
      
      const response = await apiService.get(url, {
        responseType: 'blob' 
      });
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Users exported successfully!");
    } catch (error) {
      console.error("Error exporting users:", error);
      toast.error("Failed to export users");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    updateFilters({
      ...filters,
      page: newPage
    });
  };


  useEffect(() => {
    fetchTileSizes();
  }, []);

  // Handle URL changes (e.g., when returning from edit pages)
  useEffect(() => {
    const newFilters = getInitialFilters();
    // Only update if filters have actually changed to avoid infinite loops
    if (JSON.stringify(newFilters) !== JSON.stringify(filters)) {
      setFilters(newFilters);
    }
  }, [location.search]);

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  return (
    <div className="">
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">User Management</h3>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="card-title">Filters</h4>
            <button
              className="btn btn-success"
              onClick={handleExportUsers}
              disabled={loading}
              title="Export Users to Excel"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fe fe-download me-2"></i>
                  Export Users
                </>
              )}
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <div className="form-group">
                <label>User Type</label>
                <Select
                  options={userTypeOptions}
                  value={userTypeOptions.find(
                    (opt) => opt.value === filters.user_type
                  )}
                  onChange={(selected) =>
                    updateFilters({
                      ...filters,
                      user_type: selected ? selected.value : "all",
                      page: 1, // Reset to first page when filter changes
                    })
                  }
                  isClearable={false}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Subscription Status</label>
                <Select
                  options={subscriptionStatusOptions}
                  value={subscriptionStatusOptions.find(
                    (opt) => opt.value === filters.subscription_status
                  )}
                  onChange={(selected) =>
                    updateFilters({
                      ...filters,
                      subscription_status: selected ? selected.value : "all",
                      page: 1, // Reset to first page when filter changes
                    })
                  }
                  isClearable={false}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Verify Status</label>
                <Select
                  options={verifyStatusOptions}
                  value={verifyStatusOptions.find(
                    (opt) => opt.value === filters.verify_status
                  )}
                  onChange={(selected) =>
                    updateFilters({
                      ...filters,
                      verify_status: selected ? selected.value : "all",
                      page: 1, // Reset to first page when filter changes
                    })
                  }
                  isClearable={false}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Firm Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter firm name"
                  value={filters.firm_name}
                  onChange={(e) =>
                    updateFilters({
                      ...filters,
                      firm_name: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter mobile number"
                  value={filters.mobile_number}
                  onChange={(e) =>
                    updateFilters({
                      ...filters,
                      mobile_number: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Sort By</label>
                <Select
                  options={sortOptions}
                  value={sortOptions.find(
                    (opt) => opt.value === filters.order
                  )}
                  onChange={(selected) =>
                    updateFilters({
                      ...filters,
                      order: selected ? selected.value : "",
                      page: 1, // Reset to first page when filter changes
                    })
                  }
                  isClearable={false}
                />
              </div>
            </div>
            {filters.user_type === "seller" && (
              <div className="col-md-3">
                <div className="form-group">
                  <label>Size</label>
                  <Select
                    options={getSizeOptions()}
                    value={getSizeOptions().find(
                      (opt) => opt.value === filters.size
                    )}
                    onChange={(selected) =>
                      updateFilters({
                        ...filters,
                        size: selected ? selected.value : "all",
                        page: 1, // Reset to first page when filter changes
                      })
                    }
                    isClearable={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="card-title">Users</h4>
              <span className="badge bg-primary">Total: {pagination.totalCount}</span>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">
                No users found with the selected filters.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Sr.No</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>User Type</th>
                    <th>Subscription Status</th>
                    <th>Status</th>
                    <th>Verification</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={index}>
                      <td>{index + 1 + (filters.page - 1) * filters.limit}</td>
                      <td>{user.name || user.firm_name || "N/A"}</td>
                      <td>{formatPhoneNumber(user.mobile_number)}</td>
                      <td>{getUserTypeBadge(user.user_type)}</td>
                      <td>
                        {getSubscriptionStatusBadge(user.subscription_status)}
                      </td>
                      <td>
                        {getActiveStatusBadge(user.is_disabled)}
                      </td>
                      <td>
                        {getVerificationStatusBadge(user.is_verified)}
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditUser(user)}
                            title="Edit User"
                          >
                            <i className="fe fe-edit"></i>
                          </button>
                          <button
                            className={`btn btn-sm ${user.is_disabled ? 'btn-outline-success' : 'btn-outline-danger'}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleStatus(user);
                            }}
                            title={user.is_disabled ? 'Activate User' : 'Deactivate User'}
                            disabled={loading}
                          >
                            <i className={`fe ${user.is_disabled ? 'fe-user-check' : 'fe-user-x'}`}></i>
                          </button>
                          <button
                            className={`btn btn-sm ${user.is_verified ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleVerification(user);
                            }}
                            title={user.is_verified ? 'Unverify User' : 'Verify User'}
                            disabled={loading}
                          >
                            <i className={`fas ${user.is_verified ? 'fa-ban' : 'fa-check-circle'}`}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Pagination Component */}
        <Pagination
          currentPage={filters.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          loading={loading}
          showPageInfo={true}
        />
      </div>
      {showConfirmModal && selectedUser && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Status Change</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeConfirmModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to {selectedUser.is_disabled ? 'activate' : 'deactivate'}{" "}
                  <strong>{selectedUser.name || selectedUser.firm_name || 'this user'}</strong>?
                </p>
                <p className={selectedUser.is_disabled ? 'text-success' : 'text-danger'}>
                  {selectedUser.is_disabled 
                    ? 'This will allow the user to access the platform.' 
                    : 'This will prevent the user from accessing the platform.'
                  }
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeConfirmModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${selectedUser.is_disabled ? 'btn-success' : 'btn-danger'}`}
                  onClick={confirmToggleStatus}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {selectedUser.is_disabled ? 'Activating...' : 'Deactivating...'}
                    </>
                  ) : (
                    selectedUser.is_disabled ? 'Activate User' : 'Deactivate User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="modal-backdrop fade show"></div>
      )}

      {showVerifyModal && selectedUserForVerify && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Verification Change</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeVerifyModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to {selectedUserForVerify.is_verified ? 'unverify' : 'verify'}{" "}
                  <strong>{selectedUserForVerify.name || selectedUserForVerify.firm_name || 'this user'}</strong>?
                </p>
                <p className={selectedUserForVerify.is_verified ? 'text-warning' : 'text-success'}>
                  {selectedUserForVerify.is_verified 
                    ? 'This will mark the user as unverified.' 
                    : 'This will mark the user as verified.'
                  }
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeVerifyModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${selectedUserForVerify.is_verified ? 'btn-warning' : 'btn-success'}`}
                  onClick={confirmToggleVerification}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {selectedUserForVerify.is_verified ? 'Unverifying...' : 'Verifying...'}
                    </>
                  ) : (
                    selectedUserForVerify.is_verified ? 'Unverify User' : 'Verify User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVerifyModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default UserList;
